/**
 * Auto-checks for the Apps Script course.
 *
 * The rule these follow, which is the same one the AI track follows: assert on
 * the sheet the script produced, never on the code that produced it. A trainee
 * whose script is laid out differently to ours is not wrong, and grading prose
 * is a good way to fail people who did the exercise properly.
 *
 * The one exception is the custom function, where what is IN the cell is
 * precisely the thing being taught — a column of correct labels typed by hand
 * looks identical to a column computed by a function, and only one of those is
 * the exercise.
 */

import {
  exactRow, rowCount, hasTabs, columnAllNumeric, everyCellCalls, everyCellIsFormula,
  norm, toNumber,
} from '../lib/sheetsCheck.js'
import {
  PRACTICE_TAB, TOTALS_TAB, PRACTICE_HEADERS, BAND_HEADER, TOTALS_HEADERS,
  EDITED_ROWS, STARTING_ROWS, BAND_LABELS, CUSTOM_FUNCTION, bandForTotal,
} from './scriptPractice.js'

/**
 * Module 22 — Your First Apps Script.
 *
 * Run the script you were given, then change one line and run it again. The
 * row count is what proves the edit happened; the arithmetic is what proves
 * the script ran rather than the rows being typed.
 */
export const MODULE_22_CHECKS = [
  {
    label: `The script made a tab called "${PRACTICE_TAB}"`,
    sheets: true,
    assert: hasTabs([PRACTICE_TAB]),
  },
  {
    label: `Row 1 holds ${PRACTICE_HEADERS.join(', ')}`,
    range: `${PRACTICE_TAB}!A1:D1`,
    assert: exactRow(PRACTICE_HEADERS),
  },
  {
    label: `There are ${EDITED_ROWS} rows, so the line you changed took effect`,
    range: `${PRACTICE_TAB}!A2:A200`,
    assert: (rows) => {
      const result = rowCount(EDITED_ROWS)(rows)
      if (result.ok) return result
      // "Found 10 rows" is true and unhelpful. The two ways to get here are
      // running the script before saving the edit, and editing a different
      // line — say so, because this message arrives long before the trainee
      // goes looking through the module for an explanation.
      return {
        ...result,
        hint: `The script still ran with its original ${STARTING_ROWS}. Either the edit was not saved before you pressed Run — the editor runs the last saved version — or the number you changed was not the one on the second line of buildPractice. Check Executions in the sidebar: if only one run is listed, the second one never happened.`,
      }
    },
  },
  {
    label: 'Units and Price are numbers',
    range: `${PRACTICE_TAB}!B2:B200`,
    assert: columnAllNumeric({ min: 1 }),
  },
  {
    label: 'Total is a formula, so the sheet keeps working after the script has finished',
    range: `${PRACTICE_TAB}!D2:D200`,
    formula: true,
    assert: everyCellIsFormula(),
  },
  {
    label: 'Every Total equals Units × Price',
    range: `${PRACTICE_TAB}!B2:D200`,
    assert: (rows) => {
      // [Units, Price, Total]
      const data = (rows || []).filter((r) => (r || []).length >= 3)
      if (!data.length) {
        return { ok: false, expected: 'rows with units, price and total', actual: '(nothing to check)' }
      }
      const wrong = data.filter((r) => {
        const [units, price, total] = [toNumber(r[0]), toNumber(r[1]), toNumber(r[2])]
        if (![units, price, total].every(Number.isFinite)) return true
        return Math.abs(units * price - total) > 0.01
      })
      return {
        ok: wrong.length === 0,
        expected: 'total = units × price on every row',
        actual: wrong.length
          ? `${wrong.length} row${wrong.length === 1 ? '' : 's'} do not multiply out — e.g. ${wrong[0].join(' , ')}`
          : `all ${data.length} rows multiply out`,
        hint: wrong.length
          ? 'The script computes Total from the other two columns. A row that does not multiply out was edited by hand, or the script did not finish.'
          : null,
      }
    },
  },
]

/**
 * Module 23 — Making It Repeatable.
 *
 * A custom function used in the sheet, and a menu item whose effect can be
 * seen. The daily trigger is taught but not checked: nothing a trigger does
 * before its first run is visible through the Sheets API, and a check that
 * cannot fail is worse than no check.
 */
export const MODULE_23_CHECKS = [
  {
    label: `The practice tab has a "${BAND_HEADER}" column`,
    range: `${PRACTICE_TAB}!A1:E1`,
    assert: exactRow([...PRACTICE_HEADERS, BAND_HEADER]),
  },
  {
    label: `Every cell in that column calls ${CUSTOM_FUNCTION}() rather than holding a typed answer`,
    range: `${PRACTICE_TAB}!E2:E200`,
    formula: true,
    assert: everyCellCalls(CUSTOM_FUNCTION),
  },
  {
    label: 'And every band it works out matches the total beside it',
    range: `${PRACTICE_TAB}!D2:E200`,
    assert: (rows) => {
      const data = (rows || []).filter((r) => String((r || [])[0] ?? '').trim() !== '')
      if (!data.length) {
        return { ok: false, expected: 'a band beside every total', actual: '(no totals found)' }
      }
      const wrong = data.filter((r) => norm(r[1]) !== norm(bandForTotal(toNumber(r[0]))))
      return {
        ok: wrong.length === 0,
        expected: 'every band matches its total',
        actual: wrong.length
          ? `${wrong.length} disagree — e.g. ${wrong[0][0]} is labelled "${wrong[0][1]}"`
          : `all ${data.length} agree`,
        hint: wrong.length
          ? `Check the cut-offs in ${CUSTOM_FUNCTION} — and remember a function returning nothing shows as an empty cell, not an error.`
          : null,
      }
    },
  },
  {
    label: `Your menu item built a "${TOTALS_TAB}" tab`,
    sheets: true,
    assert: hasTabs([TOTALS_TAB]),
  },
  {
    label: `The ${TOTALS_TAB} tab is headed ${TOTALS_HEADERS.join(' and ')}`,
    range: `${TOTALS_TAB}!A1:B1`,
    assert: exactRow(TOTALS_HEADERS),
  },
  {
    label: 'And its counts match the rows they came from',
    // The bands as they stand on the practice tab, and the counts the menu
    // item wrote. Recomputed rather than compared to a fixture, because the
    // trainee may have changed the row count or the data by now.
    ranges: [`${PRACTICE_TAB}!E2:E200`, `${TOTALS_TAB}!A2:B10`],
    assert: ([bandRows, totalRows]) => {
      const actual = new Map()
      ;(bandRows || []).forEach((r) => {
        const band = norm((r || [])[0])
        if (band) actual.set(band, (actual.get(band) || 0) + 1)
      })
      if (!actual.size) {
        return { ok: false, expected: 'bands to count', actual: '(the band column is empty)' }
      }
      const claimed = new Map()
      ;(totalRows || []).forEach((r) => {
        const band = norm((r || [])[0])
        if (band && !claimed.has(band)) claimed.set(band, toNumber((r || [])[1]))
      })
      const problems = []
      actual.forEach((count, band) => {
        const shown = claimed.get(band)
        if (shown === undefined) problems.push(`${band} is missing`)
        else if (shown !== count) problems.push(`${band} says ${shown}, there are ${count}`)
      })
      return {
        ok: problems.length === 0,
        expected: [...actual].map(([b, c]) => `${b} ${c}`).join(', '),
        actual: problems.length ? problems.join('; ') : 'every count agrees',
        hint: problems.length
          ? 'Run the menu item again — the counts were right when it last ran, and the rows have changed since.'
          : null,
      }
    },
  },
]

/** Only the labels are used in the module copy; kept here so both agree. */
export const PRACTICE_BAND_LABELS = BAND_LABELS
